import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getPoTrips } from '../services/api';
import { toLocalISODate } from '../utils/dateUtils';

const STATUS_META = {
  ok:         { label: 'OK',         cls: 'ok' },
  pending:    { label: 'Pending',    cls: 'warning' },
  warning:    { label: 'Warning',    cls: 'warning' },
  suspicious: { label: 'Suspicious', cls: 'suspicious' },
};

function fmtDate(raw) {
  if (!raw) return '—';
  return new Date(raw).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PoTrips() {
  const today     = toLocalISODate();
  const monthStart = today.slice(0, 7) + '-01';

  const [from, setFrom]     = useState(monthStart);
  const [to, setTo]         = useState(today);
  const [trips, setTrips]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPoTrips({ from, to });
      setTrips(Array.isArray(data) ? data : []);
    } catch {
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return trips;
    const q = search.toLowerCase();
    return trips.filter((t) =>
      (t.vehicle?.name || '').toLowerCase().includes(q) ||
      (t.vehicle?.plate || '').toLowerCase().includes(q) ||
      (t.driver?.name || '').toLowerCase().includes(q) ||
      (t.poNumbers || []).some((n) => n.toLowerCase().includes(q)) ||
      (t.purpose || '').toLowerCase().includes(q)
    );
  }, [trips, search]);

  const stats = useMemo(() => {
    const totalKM    = filtered.reduce((s, t) => s + (t.actualKM || 0), 0);
    const totalPOs   = filtered.reduce((s, t) => s + (t.poNumbers?.length || 0), 0);
    const vehicles   = new Set(filtered.map((t) => String(t.vehicle?._id)).filter(Boolean)).size;
    return { count: filtered.length, totalKM, totalPOs, vehicles };
  }, [filtered]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>PO / Challan Trips</h2>
          <p>Vehicle-out trips that are linked to a Purchase Order or Challan number.</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="card po-filter-card mb16">
        <div className="po-filter-row">
          <div className="form-group po-filter-field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="form-group po-filter-field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="form-group po-filter-field po-filter-search">
            <label>Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Vehicle, driver, PO no…"
            />
          </div>
          <div className="po-filter-action">
            <button className="btn btn-primary" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : '⟳ Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && trips.length > 0 && (
        <div className="po-stat-grid mb16">
          <div className="po-stat-card">
            <span className="po-stat-icon">📄</span>
            <div>
              <div className="po-stat-value">{stats.count}</div>
              <div className="po-stat-label">Trips</div>
            </div>
          </div>
          <div className="po-stat-card">
            <span className="po-stat-icon">🔢</span>
            <div>
              <div className="po-stat-value">{stats.totalPOs}</div>
              <div className="po-stat-label">PO / Challan Nos</div>
            </div>
          </div>
          <div className="po-stat-card">
            <span className="po-stat-icon">🚗</span>
            <div>
              <div className="po-stat-value">{stats.vehicles}</div>
              <div className="po-stat-label">Vehicles</div>
            </div>
          </div>
          <div className="po-stat-card">
            <span className="po-stat-icon">🛣</span>
            <div>
              <div className="po-stat-value">{stats.totalKM.toFixed(0)} km</div>
              <div className="po-stat-label">Total Distance</div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
          <p className="text-muted">Loading trips…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <p style={{ fontWeight: 600, marginBottom: 6 }}>No PO / Challan trips found</p>
          <p className="text-muted" style={{ fontSize: '0.9em' }}>
            {search ? 'Try adjusting your search.' : 'No trips in this date range have a PO or Challan number.'}
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Vehicle</th>
                <th>Driver</th>
                <th>Meter Out</th>
                <th>Meter In</th>
                <th>KM</th>
                <th>Purpose</th>
                <th>PO / Challan No(s)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const meta = STATUS_META[t.status] || STATUS_META.pending;
                return (
                  <tr key={t._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.date)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{t.vehicle?.name || '—'}</div>
                      {t.vehicle?.plate && (
                        <div style={{ fontSize: '0.8em', color: 'var(--muted)' }}>{t.vehicle.plate}</div>
                      )}
                    </td>
                    <td>{t.driver?.name || '—'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{t.meterOut ?? '—'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{t.meterIn ?? '—'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t.actualKM != null ? <strong>{t.actualKM} km</strong> : '—'}
                    </td>
                    <td style={{ maxWidth: 140 }}>{t.purpose || <span className="text-muted">—</span>}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {Array.isArray(t.poNumbers) && t.poNumbers.length > 0
                          ? t.poNumbers.map((n, i) => (
                              <span key={i} className="po-number-badge">{n}</span>
                            ))
                          : <span className="text-muted">—</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${meta.cls}`}>{meta.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="po-table-footer">
            {filtered.length} trip{filtered.length !== 1 ? 's' : ''}
            {search && trips.length !== filtered.length && ` (filtered from ${trips.length})`}
          </div>
        </div>
      )}
    </div>
  );
}
